import { createContext, useContext, useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { findProduct } from "@/data/mellow";

const CartContext = createContext(null);
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem("mm_cart");
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const [stock, setStock] = useState({});
  const [lastAdded, setLastAdded] = useState(null);

  const refreshStock = () => fetch(`${API}/stock`).then((r) => r.json()).then(setStock).catch(() => {});
  useEffect(() => { refreshStock(); }, []);

  useEffect(() => {
    localStorage.setItem("mm_cart", JSON.stringify(items));
  }, [items]);

  const available = (lookup_key) => (lookup_key in stock ? stock[lookup_key] : Infinity);

  const add = (lookup_key, quantity = 1) => {
    const current = items.find((i) => i.lookup_key === lookup_key)?.quantity || 0;
    const max = available(lookup_key);
    if (current + quantity > max) {
      toast.error(max === 0 ? "Ce produit est épuisé pour le moment ✿" : `Il n'en reste que ${max} en stock ✿`);
      if (current >= max) return;
      quantity = max - current;
    }
    setLastAdded({ lookup_key, at: Date.now() });
    setItems((prev) => {
      const found = prev.find((i) => i.lookup_key === lookup_key);
      if (found) return prev.map((i) => i.lookup_key === lookup_key ? { ...i, quantity: i.quantity + quantity } : i);
      return [...prev, { lookup_key, quantity }];
    });
  };
  const remove = (lookup_key) => setItems((prev) => prev.filter((i) => i.lookup_key !== lookup_key));
  const setQty = (lookup_key, q) => {
    if (q <= 0) return remove(lookup_key);
    const max = available(lookup_key);
    if (q > max) { toast.error(`Il n'en reste que ${max} en stock ✿`); q = max; }
    setItems((prev) => prev.map((i) => i.lookup_key === lookup_key ? { ...i, quantity: q } : i));
  };
  const clear = () => setItems([]);

  const totals = useMemo(() => {
    const detailed = items
      .filter((i) => findProduct(i.lookup_key) && !findProduct(i.lookup_key).comingSoon)
      .map((i) => {
        const p = findProduct(i.lookup_key);
        return { ...i, product: p, subtotal: p.price * i.quantity };
      });
    const total = detailed.reduce((s, i) => s + i.subtotal, 0);
    const count = detailed.reduce((s, i) => s + i.quantity, 0);
    return { detailed, total, count };
  }, [items]);

  return (
    <CartContext.Provider value={{ items, add, remove, setQty, clear, lastAdded, stock, available, refreshStock, ...totals }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside CartProvider");
  return ctx;
};
